import React, { useState } from "react";
import PropTypes from "prop-types";
import { DragSource, DropTarget } from "react-dnd";
import _ from "lodash";

import { withStyles } from "@material-ui/core/styles";
import Icon from "@material-ui/core/Icon";

import DeleteColumnContainer from "./deleteColumn.container";
import RuleTypeContainer from "./ruleType.container";
import RuleFormatContainer from "./ruleFormat.container";
import FieldTypeContainer from "./fieldType.container";
import DefineRuleComponent from "./defineRule.container";
import DefineMappingComponent from "../mapping/defineMapping.component";
import AppliedRulesContainer from "./appliedRules.container";
import CountedValuesComponent from "./countedValues.component";
import { itemSource, itemTarget } from "../../../helpers/dndSimple.helper";
import { Button, Badge } from "@material-ui/core";

const styles = theme => ({
  row: {
    color: "inherit",
    display: "table-row",
    height: 48,
    verticalAlign: "middle",
    width: "100%",
    outline: "none",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "light"
          ? "rgba(0, 0, 0, 0.07)" // grey[200]
          : "rgba(255, 255, 255, 0.14)"
    },
    borderBottomColor: "rgb(224, 224, 224)",
    borderBottomStyle: "solid",
    borderBottomWidth: "1px"
  },
  appliedRulesContainer: {
    "& > *": {
      float: "right"
    }
  },
  cell: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.typography.fontWeightRegular,marginTop: '15px',
  },
  hiddenCell: {
    display: "none"
  },
  skip: {
    background: theme.palette.grey["300"]
  },
  dragIcon: {
    float: "left",
    margin: ' 0 0 0 15px',
    cursor: "move",
    color: theme.palette.grey[500]
  }
});

const RuleTableRowComponent = ({
  column,
  rules,
  metadata,
  connectDragSource,
  connectDropTarget,
  connectDragPreview,
  isDragging,
  classes,
  rulesApplied,
  skippedColumnsHidden
}) => {
  const [appliedRulesShown, setAppliedRulesShown] = useState(false);

  const getAppliedRules = key =>
    _.pickBy(rules, rule => rule.attribute === key);

  const chooseClass = type => {
    if (type === "Skip") {

      if(skippedColumnsHidden) return [classes.hiddenCell]
      return [
        classes.skip,
        [classes.row]
      ];

    }

    return [classes.row];
  };
  const toggleAppliedRules = () => {
    setAppliedRulesShown(!appliedRulesShown);
  };

  if (!metadata[column.key]) return null;
  let rulesColumn;
  if (appliedRulesShown) {
    rulesColumn = (
      <div className={classes.AppliedRulesContainer}>
        <AppliedRulesContainer
          rules={getAppliedRules(column.key)}
          column={column}
          toggleContainer={toggleAppliedRules}
          rulesApplied={rulesApplied}
        />
      </div>
    );
  } else {
    const appliedRuleArraySize = _.size(getAppliedRules(column.key));
    const skippedColumn = column.type === "Skip";
    rulesColumn = (
      <div className={classes.AppliedRulesContainer}>
        <Badge
          color="secondary"
          badgeContent={appliedRuleArraySize}
          className={classes.margin}
        >
          <Button
            disabled={(appliedRuleArraySize === 0) || skippedColumn}
            onClick={toggleAppliedRules}
            variant="contained"
            color="primary"
            className={classes.button}
          >
            Applied rules
          </Button>
        </Badge>
      </div>
    );
  }
  return (
    connectDragPreview &&
    connectDragSource &&
    connectDropTarget &&
    connectDropTarget(
      connectDragPreview(
        <tr
          style={{ opacity: isDragging ? 0 : 1 }}
          className={chooseClass(metadata[column.key].type).join(" ")}
        >
          <td className={classes.cell}>
            {column.type !== "Skip"
              ? connectDragSource(
                  <span>
                    <Icon className={classes.dragIcon}>drag_indicator</Icon>
                  </span>
                )
              : null}
          </td>
          <td className={classes.cell}>
            <DeleteColumnContainer column={column} />
            {column.key}
          </td>
          <td className={classes.cell}>
            <DefineMappingComponent column={column} metadata={metadata} />
          </td>
          <td className={classes.cell}>
            <CountedValuesComponent column={metadata[column.key]}/>
          </td>
          <td
            className={!appliedRulesShown ? classes.cell : classes.hiddenCell}
          >
            <RuleTypeContainer column={column} />
          </td>
          <td
            className={!appliedRulesShown ? classes.cell : classes.hiddenCell}
          >
            <RuleFormatContainer column={column} />
          </td>
          <td
            className={!appliedRulesShown ? classes.cell : classes.hiddenCell}
          >
            <FieldTypeContainer column={column} />
          </td>
          <td>
            <DefineRuleComponent columnKey={column.key} column={column} />
          </td>
          <td colSpan="5" className={classes.cell}>
            {rulesColumn}{" "}
          </td>
        </tr>
      )
    )
  );
};

RuleTableRowComponent.propTypes = {
  column: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    format: PropTypes.string,
    fieldType: PropTypes.string,
    custom: PropTypes.bool,
    index: PropTypes.number
  }).isRequired,
  rules: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string,
    attribute: PropTypes.string,
    index: PropTypes.number
  }).isRequired,
  metadata: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    format: PropTypes.string,
    custom: PropTypes.bool,
    value: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  /* eslint-disable react/forbid-prop-types, react/no-unused-prop-types */
  index: PropTypes.number.isRequired,
  moveRule: PropTypes.func.isRequired,
  dropRule: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  rulesApplied: PropTypes.bool,
  skippedColumnsHidden: PropTypes.bool,
  /* eslint-enable */
};

export default DropTarget("row", itemTarget, connect => ({
  connectDropTarget: connect.dropTarget()
}))(
  DragSource("row", itemSource, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging()
  }))(withStyles(styles)(RuleTableRowComponent))
);
